/**
 * Deterministic whitespace-only canonicalization for governed PDF page matching.
 *
 * Allowed normalization:
 * - CR/LF normalization
 * - collapse repeated whitespace to one ASCII space
 * - trim outer whitespace
 *
 * Not applied: punctuation, case, hyphenation, ligatures, fuzzy equivalence.
 */

export function canonicalizePdfPageText(text: string): string {
  return text
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\s+/g, " ")
    .trim();
}

export type PdfTextItemCharSource = {
  itemIndex: number;
  charIndex: number;
};

export type CanonicalPdfPageText = {
  text: string;
  charToItem: ReadonlyArray<PdfTextItemCharSource | null>;
};

/**
 * Build one canonical page-text representation from PDF.js text items.
 * Inserts one ASCII space between consecutive non-empty items, then canonicalizes
 * while preserving a per-character map back to source text items.
 */
export function buildCanonicalPdfPageTextFromItems(
  items: ReadonlyArray<{ str: string }>,
): CanonicalPdfPageText {
  let raw = "";
  const rawToItem: Array<PdfTextItemCharSource | null> = [];

  for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
    const itemText = items[itemIndex]?.str ?? "";
    if (raw.length > 0 && itemText.length > 0) {
      raw += " ";
      rawToItem.push(null);
    }
    for (let charIndex = 0; charIndex < itemText.length; charIndex++) {
      raw += itemText.charAt(charIndex);
      rawToItem.push({ itemIndex, charIndex });
    }
  }

  return canonicalizePdfPageTextWithMapping(raw, rawToItem);
}

function canonicalizePdfPageTextWithMapping(
  raw: string,
  rawToItem: ReadonlyArray<PdfTextItemCharSource | null>,
): CanonicalPdfPageText {
  const charToItem: Array<PdfTextItemCharSource | null> = [];
  let canonical = "";
  let index = 0;

  while (index < raw.length && isWhitespace(raw.charAt(index))) {
    index++;
  }

  let pendingSpace = false;
  while (index < raw.length) {
    const character = raw.charAt(index);
    const source = rawToItem[index] ?? null;
    index++;

    if (isWhitespace(character)) {
      if (canonical.length > 0) {
        pendingSpace = true;
      }
      continue;
    }

    if (pendingSpace) {
      canonical += " ";
      charToItem.push(null);
      pendingSpace = false;
    }

    canonical += character;
    charToItem.push(source);
  }

  return { text: canonical, charToItem };
}

function isWhitespace(character: string): boolean {
  return /\s/.test(character);
}
